/**
 * KioskWelcome - Welcome/Splash screen with loyalty program intro
 */
import { Crown, Gift, Star, Sparkles, User, ArrowRight, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKiosk, BULACCINO_LOGO } from './KioskContext';

export default function KioskWelcome() {
  const { setView, isAccessible } = useKiosk();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-950 to-black flex items-center justify-center p-8" data-testid="kiosk-welcome">
      <div className="text-center text-white max-w-2xl">
        
        {/* TAKE AWAY ONLY - Prominent Banner at TOP */}
        <div className="mb-8 animate-pulse">
          <div className="bg-gradient-to-r from-teal-600 via-teal-500 to-teal-600 rounded-3xl p-6 border-4 border-white shadow-2xl shadow-teal-500/30">
            <h2 className={`${isAccessible ? 'text-5xl' : 'text-4xl'} font-black text-white uppercase tracking-wide mb-3`}>
              Kiosk Is For Take Away Orders Only
            </h2>
            <div className="bg-white/20 rounded-2xl px-6 py-4 mt-4">
              <p className={`${isAccessible ? 'text-3xl' : 'text-2xl'} font-bold text-white`}>
                For Dine In — Seat Yourself or See A Server
              </p>
            </div>
          </div>
        </div>

        {/* Logo with white background for visibility */}
        <div className="mb-6">
          <div className="inline-block bg-white rounded-3xl p-6 mb-4">
            <img 
              src={BULACCINO_LOGO}
              alt="Bulaccino Cafe"
              className={`${isAccessible ? 'w-56 h-56' : 'w-40 h-40'} object-contain`}
            />
          </div>
          <h1 className={`${isAccessible ? 'text-5xl' : 'text-4xl'} font-bold text-white`}>Bula, Welcome!</h1>
        </div>

        {/* Payment Notice Banner */}
        <div className="bg-gradient-to-r from-green-900/40 to-green-800/30 rounded-2xl p-6 mb-6 border border-green-500/40">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Banknote className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} text-green-400`} />
            <span className={`${isAccessible ? 'text-2xl' : 'text-xl'} font-bold text-green-200`}>Payment at Counter</span>
          </div>
          <div className="bg-black/30 rounded-xl p-4">
            <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-gray-300 mb-2`}>
              Place your order here, then pay at the <span className="font-bold text-amber-400">Cashier Counter</span>.
            </p>
            <p className={`${isAccessible ? 'text-base' : 'text-sm'} text-gray-400`}>
              Cash, EFTPOS, or M-PAiSA accepted at the counter.
            </p>
          </div>
        </div>

        {/* Already a Member Button - Moved above Rewards Program */}
        <Button
          onClick={() => setView('member-lookup')}
          className={`w-full ${isAccessible ? 'py-10 text-3xl' : 'py-8 text-2xl'} bg-primary hover:bg-primary/90 active:bg-primary/80 rounded-2xl text-white mb-6 touch-manipulation`}
          style={{ touchAction: 'manipulation' }}
          data-testid="kiosk-already-member-btn"
        >
          <User className={`${isAccessible ? 'w-10 h-10' : 'w-8 h-8'} mr-4`} />
          Already a Member? Sign In
        </Button>

        {/* Loyalty Program Banner */}
        <div className="bg-gradient-to-r from-amber-600/30 to-amber-500/20 rounded-3xl p-8 mb-6 border border-amber-500/30">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Crown className={`${isAccessible ? 'w-12 h-12' : 'w-10 h-10'} text-amber-400`} />
            <h3 className={`${isAccessible ? 'text-4xl' : 'text-3xl'} font-bold text-amber-200`}>Rewards Program</h3>
          </div>
          <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-amber-100 mb-4`}>
            Earn points with every purchase and unlock exclusive rewards!
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-black/30 rounded-xl p-4">
              <Gift className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-amber-200`}>Free Drinks</p>
            </div>
            <div className="bg-black/30 rounded-xl p-4">
              <Star className="w-8 h-8 text-amber-400 mx-auto mb-2 fill-amber-400" />
              <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-amber-200`}>VIP Discounts</p>
            </div>
            <div className="bg-black/30 rounded-xl p-4">
              <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-amber-200`}>Birthday Rewards</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <button
            onClick={() => setView('member-signup')}
            className={`w-full ${isAccessible ? 'py-10 text-3xl' : 'py-8 text-2xl'} bg-amber-600 hover:bg-amber-700 active:bg-amber-800 rounded-2xl inline-flex items-center justify-center gap-2 font-medium transition-colors kiosk-white-text touch-manipulation`}
            style={{ touchAction: 'manipulation' }}
            data-testid="kiosk-join-program-btn"
          >
            <Crown className={`${isAccessible ? 'w-10 h-10' : 'w-8 h-8'} mr-4`} />
            Join Rewards Program
          </button>
          
          {/* Continue as Guest - Increased font size by 2 sizes */}
          <Button
            onClick={() => setView('ordering')}
            variant="ghost"
            className={`w-full ${isAccessible ? 'py-10 text-4xl' : 'py-8 text-3xl'} text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/20 rounded-2xl touch-manipulation`}
            style={{ touchAction: 'manipulation' }}
            data-testid="kiosk-continue-guest-btn"
          >
            Continue as Guest
            <ArrowRight className={`${isAccessible ? 'w-10 h-10' : 'w-8 h-8'} ml-3`} />
          </Button>
        </div>
      </div>
    </div>
  );
}
